import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Loader2, MessageCircle, RefreshCw, Send, X, Check, Bot, Plug } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  ["held", "Awaiting approval"],
  ["replied", "Replied"],
  ["skipped", "Skipped"],
  ["", "All"],
];

const STATUS_STYLES = {
  held: "bg-amber-500/10 text-amber-400",
  replied: "bg-emerald-500/10 text-emerald-400",
  skipped: "bg-[#141B29] text-slate-400",
  pending: "bg-blue-500/10 text-blue-400",
  error: "bg-red-500/10 text-red-400",
};

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function FacebookDMs() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const [filter, setFilter] = useState("held");
  const [dms, setDms] = useState([]);
  const [loadingDms, setLoadingDms] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/social/meta/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Facebook status");
    }
    setLoading(false);
  }, []);

  const loadDms = useCallback(async () => {
    setLoadingDms(true);
    try {
      const { data } = await api.get(`/admin/social/meta/dms${filter ? `?status=${filter}` : ""}`);
      setDms(data.dms || []);
      const d = {};
      (data.dms || []).forEach((m) => { d[m.id] = m.draft_reply || ""; });
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load DMs");
    }
    setLoadingDms(false);
  }, [filter]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.connected) loadDms(); }, [status?.connected, filter, loadDms]);

  const toggleAutonomy = async () => {
    try {
      const { data } = await api.put("/admin/social/meta/dm-autonomy", { enabled: !status.dm_autonomy_enabled });
      setStatus((s) => ({ ...s, dm_autonomy_enabled: data.dm_autonomy_enabled }));
      toast.success(data.dm_autonomy_enabled
        ? "DM autonomy ON — Shee will reply to genuine questions automatically"
        : "DM autonomy OFF — Shee will hold every DM reply for your approval");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/admin/social/meta/dms/poll");
      toast.success(`Checked ${data.threads} chats · ${data.new} new (${data.replied} replied, ${data.held} held, ${data.skipped} skipped)`);
      await loadStatus();
      await loadDms();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reach Facebook Messenger");
    }
    setPolling(false);
  };

  const approve = async (mid) => {
    const message = (drafts[mid] || "").trim();
    if (!message) { toast.error("Nothing to send"); return; }
    setBusyId(mid);
    try {
      await api.post(`/admin/social/meta/dms/${encodeURIComponent(mid)}/reply`, { message });
      toast.success("Reply sent on Messenger");
      await loadStatus();
      await loadDms();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the reply");
    }
    setBusyId("");
  };

  const skip = async (mid) => {
    setBusyId(mid);
    try {
      await api.post(`/admin/social/meta/dms/${encodeURIComponent(mid)}/skip`);
      await loadStatus();
      await loadDms();
    } catch { toast.error("Couldn't skip"); }
    setBusyId("");
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  if (!status?.connected) {
    return (
      <div data-testid="facebook-dms" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-8 text-center space-y-2">
        <Plug className="w-8 h-8 mx-auto text-slate-500" />
        <p className="text-slate-400 font-medium">No Facebook Page connected</p>
        <p className="text-sm text-slate-500">Connect your Page on the <span className="font-medium">FB Comments</span> tab first — DMs use the same connection.</p>
      </div>
    );
  }

  return (
    <div data-testid="facebook-dms" className="space-y-6">
      {/* Connection summary + DM autonomy */}
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-white font-medium">
            <MessageCircle className="w-5 h-5 text-[#1877F2]" /> Messenger — Direct messages
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="dm-poll-btn"
              onClick={pollNow}
              disabled={polling}
              className="inline-flex items-center gap-2 rounded-full border border-[#29344D] hover:border-[#00E5FF] text-slate-300 px-4 py-2 text-sm disabled:opacity-60"
            >
              {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check now
            </button>
          </div>
        </div>
        <div className="text-sm text-slate-400">
          <span className="font-medium text-white">{status.page_name || "Connected"}</span>
          {status.last_dm_poll_at && <span className="text-xs text-slate-500"> · last checked {fmt(status.last_dm_poll_at)}</span>}
        </div>

        {status.dm_last_error ? (
          <div data-testid="dm-diagnostic-error" className="text-xs rounded-lg bg-red-500/10 text-red-400 px-3 py-2">
            <span className="font-medium">Messenger read failed:</span> {status.dm_last_error}
            <div className="text-red-400 mt-1">
              Your Page token can read comments but not Messenger. Re-generate the Page token with the
              <span className="font-medium"> pages_messaging</span> permission (and make sure your app has Advanced Access to messaging), then reconnect on the FB Comments tab.
            </div>
          </div>
        ) : (typeof status.dm_last_threads === "number" && (
          <div data-testid="dm-diagnostic-ok" className="text-xs text-slate-500">
            Last check: Facebook returned <span className="font-medium text-slate-400">{status.dm_last_threads}</span> conversation{status.dm_last_threads === 1 ? "" : "s"} · <span className="font-medium text-slate-400">{status.dm_last_msgs ?? 0}</span> readable message{status.dm_last_msgs === 1 ? "" : "s"}.
            {status.dm_last_threads > 0 && (status.dm_last_msgs ?? 0) === 0 && (
              <span className="text-amber-400"> Conversations are visible but their messages aren&rsquo;t — the token likely lacks <span className="font-medium">pages_messaging</span> / Advanced Access.</span>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between rounded-xl bg-[#0B0F16] p-4">
          <div className="flex items-start gap-3">
            <Bot className="w-5 h-5 text-[#00E5FF] mt-0.5" />
            <div>
              <div className="text-sm font-medium text-white">Autonomous DM replies</div>
              <div className="text-xs text-slate-400 max-w-md">
                When on, Shee auto-replies to genuine app / sign-up questions within strict guardrails
                (never debate or preaching), and only within Facebook&rsquo;s 24-hour window. Everything else is held for you.
              </div>
            </div>
          </div>
          <button
            data-testid="dm-autonomy-toggle"
            onClick={toggleAutonomy}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${status.dm_autonomy_enabled ? "bg-[#00E5FF]" : "bg-[#3A4763]"}`}
            aria-pressed={status.dm_autonomy_enabled}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#0E131F] transition-transform ${status.dm_autonomy_enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
      </div>

      {/* DM list */}
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6">
        <div className="flex gap-2 p-1 bg-[#141B29] rounded-xl w-fit mb-5 overflow-x-auto no-scrollbar">
          {STATUS_TABS.map(([id, label]) => (
            <button
              key={id || "all"}
              data-testid={`dm-filter-${id || "all"}`}
              onClick={() => setFilter(id)}
              className={`shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${filter === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"}`}
            >
              {label}
              {id && status.dm_counts?.[id] > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#00E5FF] text-[#07090E] text-[10px] font-semibold">{status.dm_counts[id]}</span>
              )}
            </button>
          ))}
        </div>

        {loadingDms ? (
          <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : dms.length === 0 ? (
          <p className="text-sm text-slate-500 py-8" data-testid="dm-empty">
            Nothing here yet. {filter === "held" ? "DMs awaiting your approval will show up here." : "Hit “Check now” to pull in recent messages."}
          </p>
        ) : (
          <div className="space-y-4" data-testid="dm-list">
            {dms.map((m) => (
              <div key={m.id} data-testid={`dm-${m.id}`} className="rounded-xl border border-[#1E273D] p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-white">{m.from_name}</div>
                  <div className="flex items-center gap-2">
                    {m.category && <span className="text-[11px] text-slate-500">{m.category}</span>}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[m.status] || "bg-[#141B29] text-slate-400"}`}>{m.status}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-1.5 whitespace-pre-wrap">{m.message}</p>
                <div className="text-[11px] text-slate-500 mt-1">{fmt(m.created_time)}</div>

                {m.status === "held" && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      data-testid={`dm-draft-${m.id}`}
                      value={drafts[m.id] ?? ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                      rows={2}
                      className="w-full rounded-lg border border-[#29344D] px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`dm-approve-${m.id}`}
                        onClick={() => approve(m.id)}
                        disabled={busyId === m.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-4 py-1.5 text-sm font-medium disabled:opacity-60"
                      >
                        {busyId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Approve &amp; send
                      </button>
                      <button
                        data-testid={`dm-skip-${m.id}`}
                        onClick={() => skip(m.id)}
                        disabled={busyId === m.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] hover:border-red-500/30 text-slate-400 px-4 py-1.5 text-sm disabled:opacity-60"
                      >
                        <X className="w-3.5 h-3.5" /> Skip
                      </button>
                    </div>
                  </div>
                )}
                {m.status === "replied" && m.replied_text && (
                  <div className="mt-2 flex items-start gap-1.5 text-sm text-emerald-300 bg-emerald-500/10 rounded-lg p-2.5">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{m.replied_text}</span>
                  </div>
                )}
                {m.status === "error" && m.error && <div className="mt-2 text-sm text-red-500">{m.error}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
